import React, { useCallback, useMemo } from 'react'
import { actionButtonsColumnProps, BsrsGrid, Column, EnumCell, LookupCell } from '@/components/BsrsGrid'
import { Button } from '@/components/button'
import { openDialog } from '@/store/Dialogs'
import { dialogTypes } from '@/dialogs'
import Icon from '@/components/Icon'
import classNames from 'classnames'
import { useLocalization } from '@/hooks/localization'
import { useDispatch } from '@/hooks/dispatch'
import { WhiteBox } from '@/components/Layout'
import { institutionTypes } from '@/enums/institutionTypes'
import { intMaxValue } from '@/utils/constants'
import { Row, Col, Label } from 'reactstrap'
import { openConfirmDialog } from '@/utils/dialogHelpers'
import Resx from '@/components/Resx'

export function DemographicsDynamicPermissions ({ institutionPermissions, onChange }) {
  const { localize } = useLocalization()
  const handleAdd = useCallback(item => {
    onChange([...institutionPermissions, item])
  }, [institutionPermissions, onChange])
  const handleEdit = useCallback(item => {
    const index = institutionPermissions.findIndex(x => x.id === item.id)
    const items = [...institutionPermissions]
    items.splice(index, 1, item)
    onChange(items)
  }, [institutionPermissions, onChange])

  const [showDialog] = useDispatch([openDialog])

  const institutionPermissionDataSource = useMemo(() => institutionPermissions.filter(x => !x.isCbiPermission),
    [institutionPermissions])

  const cbiPermission = useMemo(() => institutionPermissions.find(x => x.isCbiPermission), [institutionPermissions])

  const onAddClick = useCallback(() => showDialog({
    dialogType: dialogTypes.institutionPermission,
    institutionPermission: {},
    onAction: handleAdd
  }), [handleAdd, showDialog])

  const onAddInternalClick = useCallback(() => showDialog({
    dialogType: dialogTypes.individualPermission,
    institutionPermission: {},
    onAction: handleAdd
  }), [handleAdd, showDialog])

  const onEditInternalClick = useCallback(() => showDialog({
    dialogType: dialogTypes.individualPermission,
    institutionPermission: cbiPermission,
    onAction: handleEdit
  }), [cbiPermission, handleEdit, showDialog])

  const onDeleteInternalClick = useCallback(() => {
    openConfirmDialog({
      customConfirmText: 'Are you sure you want to delete CBI permissions?',
      onAction: () => onChange(institutionPermissions.filter(x => !x.isCbiPermission))
    })
  }, [institutionPermissions, onChange])

  const actionProps = actionButtonsColumnProps([
    { icon: 'pencil',
      onClick: item => {
        showDialog({
          dialogType: dialogTypes.institutionPermission,
          institutionPermission: item,
          onAction: handleEdit
        })
      }
    },
    {
      onDelete: item => {
        onChange(institutionPermissions.filter(x => x.id !== item.id))
      }
    }
  ])

  return (
    <React.Fragment>
      <WhiteBox
        header={localize('Permissions.InstitutionPermissions')}
        buttons={<Button icon='plus' color='primary' className='btn-sm' onClick={onAddClick}>{localize('Common.Add')}</Button>}
        transparent headerClass='no-uppercase'>
        <BsrsGrid items={institutionPermissionDataSource} simpleView>
          <Column field='institutionType' title={localize('Permissions.InstitutionType')} cell={EnumCell({ localize, enumName: institutionTypes.enumName })} />
          <Column field='institution' title={localize('Permissions.Institution')} cell={LookupCell} />
          <Column field='bankType' title={localize('Permissions.BankType')} cell={LookupCell} />
          <Column field='companyType' title={localize('Permissions.CompanyType')} cell={LookupCell} />
          <Column field='ownershipType' title={localize('Permissions.OwnershipType')} cell={LookupCell} />
          <Column field='individualReadOnly' title={localize('Permissions.IndividualRShort')} cell={PermissionCell} width='60px' />
          <Column field='individualEdit' title={localize('Permissions.IndividualEShort')} cell={PermissionCell} width='60px' />
          <Column field='institutionReadOnly' title={localize('Permissions.InstitutionRShort')} cell={PermissionCell} width='60px' />
          <Column field='institutionEdit' title={localize('Permissions.InstitutionEShort')} cell={PermissionCell} width='60px' />
          <Column title={' '} {...actionProps} />
        </BsrsGrid>
      </WhiteBox>
      <WhiteBox buttons={<InternalPermissionButtons cbiPermission={cbiPermission} onAddInternalClick={onAddInternalClick} onEditInternalClick={onEditInternalClick} onDeleteInternalClick={onDeleteInternalClick} />}
        header={'Cbi Permissions'}
        transparent headerClass='no-uppercase'>
        {cbiPermission ? <Row>
          <Col><Label><Resx name='Permissions.IndividualR' />  </Label><PermissionCheckbox value={cbiPermission.individualReadOnly} /></Col>
          <Col><Label><Resx name='Permissions.IndividualE' />  </Label><PermissionCheckbox value={cbiPermission.individualEdit} /></Col>
          <Col><Label><Resx name='Permissions.InstitutionR' />  </Label><PermissionCheckbox value={cbiPermission.institutionReadOnly} /></Col>
          <Col><Label><Resx name='Permissions.InstitutionE' />  </Label><PermissionCheckbox value={cbiPermission.institutionEdit} /></Col>
        </Row> : null}
      </WhiteBox>
    </React.Fragment>
  )
}

function InternalPermissionButtons ({ cbiPermission, onAddInternalClick, onEditInternalClick, onDeleteInternalClick }) {
  return !cbiPermission
    ? <Button onClick={onAddInternalClick} icon='plus' color='primary' className='btn-sm'>Add</Button>
    : <React.Fragment>
      <Button onClick={onEditInternalClick} icon='pencil' color='primary' className='btn-sm'>Edit</Button>
      <Button onClick={onDeleteInternalClick} icon='trash' color='danger' className='btn-sm'>Delete</Button>
    </React.Fragment>
}

function PermissionCell ({ field, dataItem }) {
  const value = dataItem[field] || 0
  return <td><PermissionCheckbox value={value} /></td>
}

function PermissionCheckbox ({ value }) {
  return <span className={classNames('permission-box', { 'permission-box--checked': value !== 0 })}>
    {value === intMaxValue ? <Icon name='check' /> : value !== 0 && <Icon name='minus' />}
  </span>
}



// WEBPACK FOOTER //
// ./src/internal-app/pages/jobPositionPermissions/demographicsPermissions.js